import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

/**
 * RebuildSparkline — compact 12-week v2 Rebuild Score trend.
 * Renders an inline SVG polyline. Points that are FUTURE or empty render as gaps.
 */
export function RebuildSparkline() {
  const [weeks, setWeeks] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const { data } = await api.get("/rebuild-score/history");
        if (ok) setWeeks(data.weeks || []);
      } catch (e) {
        if (ok) setErr(e?.response?.data?.detail || "unable to load");
      }
    })();
    return () => { ok = false; };
  }, []);

  const { path, dots, avg, best } = useMemo(() => {
    if (!weeks || !weeks.length) return { path: "", dots: [], avg: 0, best: 0 };
    const W = 320, H = 60, pad = 4;
    const step = (W - pad * 2) / 11;
    const y = (v) => H - pad - ((v || 0) / 100) * (H - pad * 2);
    const dots = [];
    let d = "";
    let sum = 0, ct = 0, best = 0;
    weeks.forEach((w, i) => {
      const x = pad + i * step;
      if (w.status === "future" || w.score == null) return;
      const yy = y(w.score);
      dots.push({ x, y: yy, week: w.week, score: w.score, status: w.status });
      d += d ? ` L ${x} ${yy}` : `M ${x} ${yy}`;
      sum += w.score; ct += 1;
      if (w.score > best) best = w.score;
    });
    return { path: d, dots, avg: ct ? Math.round(sum / ct) : 0, best };
  }, [weeks]);

  if (err) return null;
  if (!weeks) return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="rebuild-sparkline">
      <div className="label-eyebrow">12-Week Rebuild Trend</div>
      <div className="mt-3 h-16 rounded-md bg-muted animate-pulse" />
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="rebuild-sparkline">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">12-Week Rebuild Trend · v2</div>
          <p className="mt-1 text-xs text-muted-foreground">Weekly execution score — behaviour adherence only.</p>
        </div>
        <div className="flex gap-4 text-[11px] uppercase tracking-widest">
          <span>Avg <span className="wordmark text-lg text-foreground normal-case">{avg}</span></span>
          <span>Best <span className="wordmark text-lg text-primary normal-case">{best}</span></span>
        </div>
      </div>
      <div className="mt-4 overflow-x-auto">
        <svg viewBox="0 0 320 60" width="100%" height="60" data-testid="sparkline-svg">
          {/* thresholds */}
          {[40, 65, 85].map((t) => (
            <line key={t} x1="4" x2="316" y1={60 - 4 - (t / 100) * 52} y2={60 - 4 - (t / 100) * 52}
              stroke="hsl(var(--border))" strokeDasharray="2 4" />
          ))}
          {path && (
            <path d={path} fill="none" stroke="hsl(var(--primary))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          )}
          {dots.map((p) => (
            <circle key={p.week} cx={p.x} cy={p.y} r="3.5" fill="hsl(var(--primary))" data-testid={`sparkline-dot-${p.week}`}>
              <title>W{p.week} · {p.score}/100</title>
            </circle>
          ))}
        </svg>
      </div>
      <div className="mt-2 flex justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
        {weeks.map((w) => <span key={w.week}>W{w.week}</span>)}
      </div>
    </div>
  );
}
